/* import __COLOCATED_TEMPLATE__ from './deploy.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Tab } from 'embercom/components/standalone/common/tab-control';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';

type DeployStep = Required<Tab> & {
  value: 'integrate' | 'data' | 'workflow' | 'install' | 'test' | 'go-live';
};

export interface Args {
  activeStep: DeployStep['value'];
  section: string | null;
  setSection?: (section: string | null) => void;
}

const STEPS = [
  {
    label: (self: Deploy) => self.intl.t('standalone.intercom-messenger.deploy.tabs.connect'),
    value: 'integrate',
    path: 'apps.app.standalone.deploy.intercom-messenger.integrate',
    isCompleted: (self: Deploy) => self.config.isIntegrated,
    isWarning: (self: Deploy) => self.showOAuthWarning,
    show: (_: Deploy) => true,
  },
  {
    label: (self: Deploy) => self.intl.t('standalone.intercom-messenger.deploy.tabs.data'),
    value: 'data',
    path: 'apps.app.standalone.deploy.intercom-messenger.data',
    isCompleted: (self: Deploy) => self.isDataStepComplete,
    show: (self: Deploy) => self.app.hasStandalonePlatform('zendesk'),
  },
  {
    label: (self: Deploy) => self.intl.t('standalone.intercom-messenger.deploy.tabs.workflow'),
    value: 'workflow',
    path: 'apps.app.standalone.deploy.intercom-messenger.workflow',
    isCompleted: (self: Deploy) => self.isWorkflowStepComplete,
    show: (_: Deploy) => true,
  },
  {
    label: (self: Deploy) => self.intl.t('standalone.intercom-messenger.deploy.tabs.install'),
    value: 'install',
    path: 'apps.app.standalone.deploy.intercom-messenger.install',
    isCompleted: (self: Deploy) => self.app.hasAnyInstalledAtDate,
    show: (_: Deploy) => true,
  },
  {
    label: (self: Deploy) => self.intl.t('standalone.intercom-messenger.deploy.tabs.test'),
    value: 'test',
    path: 'apps.app.standalone.deploy.intercom-messenger.test',
    isCompleted: (self: Deploy) =>
      self.config.isMessengerInTestMode ||
      self.config.isMessengerEnabled ||
      (self.isSelectedAfter('test') && self.areBeforeStepsComplete('test')),
    show: (_: Deploy) => true,
  },
  {
    label: (self: Deploy) => self.intl.t('standalone.intercom-messenger.deploy.tabs.go-live'),
    value: 'go-live',
    path: 'apps.app.standalone.deploy.intercom-messenger.go-live',
    isCompleted: (self: Deploy) => self.config.isMessengerEnabled,
    show: (_: Deploy) => true,
  },
];

export default class Deploy extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @tracked selectedStepValue = this.args.activeStep;

  get selectedStep() {
    return (
      this.deploySteps.find((step) => step.value === this.selectedStepValue) || this.deploySteps[0]
    );
  }

  get app() {
    return this.finStandaloneService.app;
  }

  get config() {
    return this.finStandaloneService.intercomMessengerConfig;
  }

  /**
   * Returns the base conditions for a step to be completed. This is used to determine if the step is complete based on the previous steps and the app's installed at date.
   * @param stepName - The name of the step to check.
   * @returns True if the next step for the current step is selected and all previous steps are complete, or if the app has any installed at date.
   */
  baseConditionsForStepCompletion(stepName: DeployStep['value']): boolean {
    return (
      (this.isSelectedAfter(stepName) && this.areBeforeStepsComplete(stepName)) ||
      this.app.hasAnyInstalledAtDate
    );
  }

  get isWorkflowStepComplete() {
    return this.baseConditionsForStepCompletion('workflow') && this.config.isWorkflowValid;
  }

  get isDataStepComplete() {
    return this.baseConditionsForStepCompletion('data');
  }

  get showOAuthWarning() {
    return this.finStandaloneService.platform.showOauthBanner;
  }

  isSelectedAfter(value: DeployStep['value']): boolean {
    if (!this.selectedStepValue) {
      return false;
    }

    let values = STEPS.map((step) => step.value);
    return values.indexOf(this.selectedStepValue) > values.indexOf(value);
  }

  areBeforeStepsComplete(value: DeployStep['value']): boolean {
    let values = STEPS.map((step) => step.value);
    let index = values.indexOf(value);
    return STEPS.slice(0, index).every((step) => step.isCompleted(this));
  }

  get deploySteps(): DeployStep[] {
    return STEPS.filter((step) => step.show(this)).map((step, index) => {
      return {
        stepNumber: index + 1,
        value: step.value,
        label: step.label(this),
        path: step.path,
        isCompleted: step.isCompleted(this),
        isWarning: step.isWarning?.(this) ?? false,
      } as DeployStep;
    });
  }

  get helpArticleId(): number | undefined {
    if (this.app.hasStandalonePlatform('zendesk')) {
      return 10614196;
    }

    return undefined;
  }

  @action
  setStep(stepValue: DeployStep['value']) {
    this.selectedStepValue = stepValue;
    if (this.selectedStep) {
      this.router.transitionTo(this.selectedStep.path);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::IntercomMessenger::Deploy': typeof Deploy;
    'standalone/intercom-messenger/deploy': typeof Deploy;
  }
}
